import React, {useState, useEffect} from 'react'
import {searchEmployees} from '../../../../utils/search'
import {SearchInput, SEARCH_INPUT_TYPE} from '../../../SearchInput/SearchInput'

import VisitorCard from './VisitorCard/VisitorCard'
import {AutoSizer, List} from 'react-virtualized'
import NewVisitor from './NewVisitor/NewVisitor'
import Spinner from '../../../Spinner/Spinner'
import {useDebounce} from 'use-debounce'
import {_L, useLocaleState} from '../../../../context/LocaleContext'
import styled from 'styled-components'

const VisitorsList = ({
  onSelectVisitor,
  newVisitorCreated,
  label,
  employeesOnly
}) => {
  const [searchValue, setSearchValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isNewVisitor, setIsNewVisitor] = useState(false)
  const [searchResult, setSearchResult] = useState([])
  const [searchTerm] = useDebounce(searchValue, 300)
  const localeState = useLocaleState()

  useEffect(
    () => {
      setIsLoading(true)
      searchEmployees({searchTerm}).then(data => {
        setIsLoading(false)
        setSearchResult(data)
      })
    },
    [searchTerm]
  )

  const selectVisitor = ({visitor}) => {
    if (visitor.isNew) {
      setIsNewVisitor(true)
    } else {
      onSelectVisitor(visitor)
    }
  }

  const onNewVisitorCreated = visitor => {
    let newVisitor = {
      isVisitor: true,
      visitorName: `${visitor['visitors.name_first']} ${
        visitor['visitors.name_last']
      }`,
      dvId: visitor['visitors.dv_id'],
      dpId: visitor['visitors.dp_id'],
      visitorId: visitor['visitors.visitor_id'],
      email: visitor['visitors.email'],
      company: visitor['visitors.company'],
      visitorPhone: visitor['visitors.cellular_number']
    }

    newVisitorCreated(newVisitor)
  }

  // Add the New visitor button

  if (!employeesOnly && searchResult.length > 0 && !searchResult[0].isNew) {
    searchResult.unshift({isNew: true})
  }

  const ITEM_COUNT = searchResult.length

  return !isNewVisitor ? (
    <Container>
      <SearchContainer>
        <SearchInput
          type={SEARCH_INPUT_TYPE.CONTENT}
          onSearchChange={e => setSearchValue(e.target.value)}
          placeholder={_L({
            key: 'Search for an employee or visitor',
            localeState
          })}
          value={searchValue}
        />
      </SearchContainer>

      <LabelContainer>
        <TitleText>{label}</TitleText>
      </LabelContainer>

      <CardsContainer>
        <div style={{width: '100%', height: '100%'}}>
          {isLoading ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                width: '100%',
                justifyContent: 'center'
              }}
            >
              <Spinner color="#3395fe" />
            </div>
          ) : (
            <AutoSizer>
              {({height, width}) => {
                let rowHeight = width < 440 ? 66 : 100
                let itemWidth = width < 440 ? width : 320
                const itemsPerRow = Math.floor(width / itemWidth)
                const rowCount = Math.ceil(ITEM_COUNT / itemsPerRow)

                return (
                  <StyledList
                    width={width}
                    height={height}
                    rowCount={rowCount}
                    rowHeight={rowHeight}
                    rowRenderer={({index, key, style}) => {
                      const items = []
                      const fromIndex = index * itemsPerRow
                      const toIndex = Math.min(
                        fromIndex + itemsPerRow,
                        ITEM_COUNT
                      )

                      for (let i = fromIndex; i < toIndex; i++) {
                        let item = searchResult[i]
                        items.push(
                          <VisitorCard
                            key={i}
                            visitor={item}
                            onClick={selectVisitor}
                          />
                        )
                      }

                      return (
                        <StyledRow key={key} style={style}>
                          {items}
                        </StyledRow>
                      )
                    }}
                  />
                )
              }}
            </AutoSizer>
          )}
        </div>
      </CardsContainer>
    </Container>
  ) : (
    <Container>
      <NewVisitorContainer>
        <NewVisitor newVisitorCreated={onNewVisitorCreated} />
      </NewVisitorContainer>
    </Container>
  )
}

VisitorsList.defaultProps = {
  label: 'Booking space for:',
  employeesOnly: false
}

export default VisitorsList

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f3f5fb;
  border-radius: 6px;
`
const TitleText = styled.div`
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.3px;
  padding-left: 50px;
  color: #a4a9b7;

  @media (max-width: 420px) {
    padding-left: 12px;
  }
`

const StyledList = styled(List)`
  box-sizing: border-box;
  height: 100%;
  width: 100%;
`

const StyledRow = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
`

const NewVisitorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`

const CardsContainer = styled.div`
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  overflow-y: auto;
  margin-top: 20px;
  width: 100%;
  height: calc(100vh - 400px);
  margin-bottom: 40px;

  @media (max-width: 420px) {
    margin-top: 10px;
  }
`

const SearchContainer = styled.div`
  padding: 60px 20px 0 20px;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 420px) {
    padding: 26px 12px 10px 12px;
  }
`

const LabelContainer = styled.div`
  padding-top: 20px;

  @media (max-width: 420px) {
    padding-top: 10px;
  }
`
